export type Environment = 'production' | 'staging' | 'development';

/**
 * Get environment variable value
 * Works in both Vite (import.meta.env) and Jest (process.env) environments
 */
function getEnvVar(key: string): string | undefined {
  // In test environment (Jest), use process.env
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
    return process.env[key];
  }
  // In Vite (browser), import.meta.env is available and replaced at build
  // time. We reference `import.meta.env` directly (never a bare `import.meta`):
  // Vite always defines it, and under ts-jest the `jest-import-meta-transformer`
  // rewrites `import.meta.env` to `process.env`. A bare `typeof import.meta`
  // is NOT rewritten and crashes the CommonJS test build, so it is avoided.
  if (import.meta.env) {
    // Map common keys to Vite's env format
    if (key === 'VITE_ENV_NAME') {
      return import.meta.env.VITE_ENV_NAME;
    }
    if (key === 'MODE') {
      return import.meta.env.MODE;
    }
    // For other keys, try to access directly using bracket notation
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (import.meta.env as any)[key] as string | undefined;
  }
  // Fallback to process.env if available (Node.js environments)
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  return undefined;
}

export function getEnvironmentName(): Environment {
  const raw = (getEnvVar('VITE_ENV_NAME') ??
    getEnvVar('MODE') ??
    'development') as string;
  return raw === 'production' || raw === 'staging'
    ? (raw as Environment)
    : 'development';
}

export function isTelemetryEnabled(): boolean {
  const env = getEnvironmentName();
  return env === 'production' || env === 'staging';
}

export function getTelemetryEnvironment(): string {
  return getEnvironmentName();
}
